import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./stores/authStore";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import RoleBasedDashboard from "./components/RoleBasedDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import Students from "./pages/Students";
import Academics from "./pages/Academics";
import Financial from "./pages/Financial";
import Library from "./pages/Library";
import HostelPage from "./pages/Hostel";
import Transport from "./pages/Transport";
import Medical from "./pages/Medical";
import Curriculum from "./pages/Curriculum";
import SyllabusManagement from "./pages/SyllabusManagement";
import ClassStreamsSetup from "./pages/ClassStreamsSetup";
import ParentsGuardians from "./pages/ParentsGuardians";
import Inventory from "./pages/Inventory";
import Cafeteria from "./pages/Cafeteria";
import Sports from "./pages/Sports";
import Examinations from "./pages/Examinations";
import Alumni from "./pages/Alumni";
import StaffPage from "./pages/Staff";
import HRSetup from "./pages/HRSetup";
import Appraisals from "./pages/Appraisals";
import AppraisalDetail from "./pages/AppraisalDetail";
import AppraisalTemplates from "./pages/AppraisalTemplates";
import Facilities from "./pages/Facilities";
import Counseling from "./pages/Counseling";
import Scholarships from "./pages/Scholarships";
import Meetings from "./pages/Meetings";
import Events from "./pages/Events";
import Bulletin from "./pages/Bulletin";
import Emergency from "./pages/Emergency";
import Uniforms from "./pages/Uniforms";
import Analytics from "./pages/Analytics";
import Communication from "./pages/Communication";
import Administration from "./pages/Administration";
import Permissions from "./pages/Permissions";
import MyApprovals from "./pages/MyApprovals";
import PaymentCallback from "./pages/PaymentCallback";
import Attendance from "./pages/Attendance";
import Notifications from "./pages/Notifications";
import Reports from "./pages/Reports";
import StudentLifecycle from "./pages/StudentLifecycle";
import Disciplinary from "./pages/Disciplinary";
import StudentGamification from "./pages/StudentGamification";
import ClassTeacherDashboard from "./pages/ClassTeacherDashboard";
import TimetableManagement from "./pages/Timetable";
import SchoolDoctorDashboard from "./pages/SchoolDoctorDashboard";
import TeacherDashboard from "./pages/dashboards/TeacherDashboard";
import DirectorOfStudiesDashboard from "./pages/dashboards/DirectorOfStudiesDashboard";
import ParentStudentPortal from "./pages/Portal";
import BursarDashboard from "./pages/dashboards/BursarDashboard";
import HeadmasterDashboard from "./pages/dashboards/HeadmasterDashboard";
import LibrarianDashboard from "./pages/dashboards/LibrarianDashboard";
import MedicalDashboard from "./pages/dashboards/MedicalDashboard";
import TransportDashboard from "./pages/dashboards/TransportDashboard";
import UniformDashboard from "./pages/dashboards/UniformDashboard";
import HostelMatronDashboard from "./pages/dashboards/HostelMatronDashboard";
import HostelCustodianDashboard from "./pages/dashboards/HostelCustodianDashboard";
import KitchenManagerDashboard from "./pages/dashboards/KitchenManagerDashboard";
import CafeteriaManagerDashboard from "./pages/dashboards/CafeteriaManagerDashboard";
import SportsMasterDashboard from "./pages/dashboards/SportsMasterDashboard";
import UniformManagerDashboard from "./pages/dashboards/UniformManagerDashboard";
import InventoryManagerDashboard from "./pages/dashboards/InventoryManagerDashboard";
import SchoolCounsellorDashboard from "./pages/dashboards/SchoolCounsellorDashboard";
import TransportManagerDashboard from "./pages/dashboards/TransportManagerDashboard";
import BusDriverDashboard from "./pages/dashboards/BusDriverDashboard";
import DisciplinaryCommitteeDashboard from "./pages/dashboards/DisciplinaryCommitteeDashboard";
import Profile from "./pages/Profile";
import PublicAdmissions from "./pages/PublicAdmissions";
import Admissions from "./pages/Admissions";
import AuditLog from "./pages/AuditLog";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import GovernanceDashboard from "./pages/GovernanceDashboard";

function App() {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && user?.must_change_password) {
    return <Layout><Routes><Route path="/profile" element={<Profile />} /><Route path="*" element={<Navigate to="/profile" replace />} /></Routes></Layout>;
  }

  if (isAuthenticated) {
    return (
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* A reset link opened while still signed in on this browser. */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<RoleBasedDashboard />} />
          <Route path="/apply" element={<PublicAdmissions />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                ]}
              >
                <Admissions />
              </ProtectedRoute>
            }
          />
          <Route path="/audit-log" element={<ProtectedRoute capability="financial.view_audit_logs"><AuditLog /></ProtectedRoute>} />
          <Route
            path="/reports/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                  "principal",
                  "cfo",
                  "bursar",
                ]}
              >
                <Reports />
              </ProtectedRoute>
            }
          />

          {/* Student Management Routes */}
          <Route
            path="/students/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                ]}
              >
                <Students />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                ]}
              >
                <Attendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student-lifecycle/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                ]}
              >
                <StudentLifecycle />
              </ProtectedRoute>
            }
          />

          {/* Academic Routes */}
          <Route
            path="/academics/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                ]}
              >
                <Academics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/curriculum/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                ]}
              >
                <Curriculum />
              </ProtectedRoute>
            }
          />
          <Route
            path="/syllabi/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                ]}
              >
                <SyllabusManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/class-setup/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                ]}
              >
                <ClassStreamsSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parents/*"
            element={
              <ProtectedRoute roles={["admin", "director", "head_teacher"]}>
                <ParentsGuardians />
              </ProtectedRoute>
            }
          />
          <Route
            path="/examinations/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                ]}
              >
                <Examinations />
              </ProtectedRoute>
            }
          />

          {/* Financial Routes */}
          <Route
            path="/financial/*"
            element={
              <ProtectedRoute
                roles={["admin", "director", "principal", "cfo", "bursar"]}
              >
                <Financial />
              </ProtectedRoute>
            }
          />
          <Route
            path="/governance"
            element={
              <ProtectedRoute capability="financial.view_governance_dashboard">
                <GovernanceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/callback"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "bursar",
                  "parent",
                  "student",
                ]}
              >
                <PaymentCallback />
              </ProtectedRoute>
            }
          />

          {/* Support Services */}
          <Route
            path="/library/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "librarian",
                  "teacher",
                  "class_teacher",
                  "parent",
                  "student",
                ]}
              >
                <Library />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hostel/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "hostel_matron",
                  "hostel_custodian",
                  "parent",
                  "student",
                ]}
              >
                <HostelPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transport/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "transport_manager",
                  "bus_driver",
                  "parent",
                  "student",
                ]}
              >
                <Transport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/medical/*"
            element={
              <ProtectedRoute
                roles={["admin", "director", "head_teacher", "school_doctor"]}
              >
                <Medical />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sports/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "sports_master",
                  "teacher",
                  "class_teacher",
                  "parent",
                  "student",
                ]}
              >
                <Sports />
              </ProtectedRoute>
            }
          />

          {/* Administrative Routes */}
          <Route
            path="/staff/*"
            element={
              <ProtectedRoute>
                <StaffPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr-setup"
            element={
              // Whoever holds access.staff.manager: HR is the principal's job,
              // not the head teacher's (core migrations 0024 and 0026).
              <ProtectedRoute roles={["admin", "director", "principal"]}>
                <HRSetup />
              </ProtectedRoute>
            }
          />
          {/* Appraisals. Open to every signed-in user: the register itself is
              filtered per person server-side, so a teacher sees their own and
              a supervisor sees the ones they conduct. */}
          <Route
            path="/appraisals"
            element={
              <ProtectedRoute>
                <Appraisals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/appraisals/:id"
            element={
              <ProtectedRoute>
                <AppraisalDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/appraisal-templates"
            element={
              <ProtectedRoute
                capability={[
                  "access.appraisal.manage_templates",
                  "access.appraisal.manage_cycles",
                ]}
              >
                <AppraisalTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "cfo",
                  "inventory_manager",
                ]}
              >
                <Inventory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facilities/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "teacher",
                  "class_teacher",
                  "head_of_department",
                ]}
              >
                <Facilities />
              </ProtectedRoute>
            }
          />

          {/* Communication & Events */}
          <Route
            path="/communication/*"
            element={
              <ProtectedRoute>
                <Communication />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                  "parent",
                  "student",
                ]}
              >
                <Events />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulletin/*"
            element={
              <ProtectedRoute>
                <Bulletin />
              </ProtectedRoute>
            }
          />

          {/* Discipline & Counseling */}
          <Route
            path="/disciplinary/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                  "disciplinary_committee",
                ]}
              >
                <Disciplinary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/counseling/*"
            element={
              <ProtectedRoute>
                <Counseling />
              </ProtectedRoute>
            }
          />

          {/* Notifications */}
          <Route
            path="/notifications/*"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />

          {/* Analytics */}
          <Route
            path="/analytics/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                ]}
              >
                <Analytics />
              </ProtectedRoute>
            }
          />

          {/* Parent/Student Portal */}
          <Route
            path="/portal/*"
            element={
              <ProtectedRoute roles={["parent", "student"]}>
                <ParentStudentPortal />
              </ProtectedRoute>
            }
          />

          {/* Administration */}
          <Route
            path="/administration/*"
            element={
              <ProtectedRoute roles={["admin", "director", "head_teacher"]}>
                <Administration />
              </ProtectedRoute>
            }
          />

          {/* Capability permissions */}
          <Route
            path="/permissions"
            element={
              <ProtectedRoute capability="core.assign_permissions">
                <Permissions />
              </ProtectedRoute>
            }
          />

          {/* Unified cross-document approvals queue */}
          <Route
            path="/my-approvals"
            element={
              <ProtectedRoute
                capability={["financial.review_transactions", "financial.approve_transactions"]}
              >
                <MyApprovals />
              </ProtectedRoute>
            }
          />

          {/* Additional Services */}
          <Route
            path="/cafeteria/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "cafeteria_manager",
                  "kitchen_manager",
                  "parent",
                  "student",
                ]}
              >
                <Cafeteria />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scholarships/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "bursar",
                  "parent",
                ]}
              >
                <Scholarships />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meetings/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                  "parent",
                ]}
              >
                <Meetings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emergency/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "director_of_studies",
                  "teacher",
                  "class_teacher",
                  "head_of_department",
                  "school_doctor",
                  "hostel_matron",
                  "transport_manager",
                  "disciplinary_committee",
                ]}
              >
                <Emergency />
              </ProtectedRoute>
            }
          />
          <Route
            path="/uniforms/*"
            element={
              <ProtectedRoute
                roles={[
                  "admin",
                  "director",
                  "head_teacher",
                  "uniform_manager",
                  "parent",
                  "student",
                ]}
              >
                <Uniforms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alumni/*"
            element={
              <ProtectedRoute>
                <Alumni />
              </ProtectedRoute>
            }
          />

          {/* Student Gamification */}
          <Route
            path="/student/gamification/*"
            element={
              <ProtectedRoute roles={["student"]}>
                <StudentGamification />
              </ProtectedRoute>
            }
          />

          {/* Class Teacher Dashboard */}
          <Route
            path="/class-teacher/dashboard/*"
            element={
              <ProtectedRoute roles={["class_teacher"]}>
                <ClassTeacherDashboard />
              </ProtectedRoute>
            }
          />

          {/* Timetable Management */}
          <Route
            path="/timetable/*"
            element={
              <ProtectedRoute
                roles={[
                  "director_of_studies",
                  "head_teacher",
                  "director",
                  "admin",
                  "head_of_department",
                  "class_teacher",
                  "teacher",
                  "parent",
                  "student",
                ]}
              >
                <TimetableManagement />
              </ProtectedRoute>
            }
          />

          {/* Teacher Dashboard */}
          <Route
            path="/teacher/dashboard/*"
            element={
              <ProtectedRoute roles={["teacher", "head_of_department"]}>
                <TeacherDashboard />
              </ProtectedRoute>
            }
          />

          {/* Director of Studies Dashboard */}
          <Route
            path="/director-of-studies/dashboard/*"
            element={
              <ProtectedRoute
                roles={[
                  "director_of_studies",
                  "head_teacher",
                  "director",
                  "admin",
                ]}
              >
                <DirectorOfStudiesDashboard />
              </ProtectedRoute>
            }
          />

          {/* School Doctor Dashboard */}
          <Route
            path="/doctor/dashboard/*"
            element={
              <ProtectedRoute roles={["school_doctor", "admin"]}>
                <SchoolDoctorDashboard />
              </ProtectedRoute>
            }
          />

          {/* Bursar Dashboard */}
          <Route
            path="/bursar/dashboard/*"
            element={
              <ProtectedRoute roles={["bursar", "admin"]}>
                <BursarDashboard />
              </ProtectedRoute>
            }
          />

          {/* Headmaster Dashboard */}
          <Route
            path="/headmaster/dashboard/*"
            element={
              <ProtectedRoute roles={["head_teacher", "director", "admin"]}>
                <HeadmasterDashboard />
              </ProtectedRoute>
            }
          />

          {/* Librarian Dashboard */}
          <Route
            path="/librarian/dashboard/*"
            element={
              <ProtectedRoute roles={["librarian", "admin"]}>
                <LibrarianDashboard />
              </ProtectedRoute>
            }
          />

          {/* Medical Dashboard */}
          <Route
            path="/medical/dashboard/*"
            element={
              <ProtectedRoute roles={["school_doctor", "admin"]}>
                <MedicalDashboard />
              </ProtectedRoute>
            }
          />

          {/* Transport Dashboard */}
          <Route
            path="/transport/dashboard/*"
            element={
              <ProtectedRoute roles={["transport_manager", "admin"]}>
                <TransportDashboard />
              </ProtectedRoute>
            }
          />

          {/* Uniform Dashboard */}
          <Route
            path="/uniform/dashboard/*"
            element={
              <ProtectedRoute roles={["uniform_manager", "admin"]}>
                <UniformDashboard />
              </ProtectedRoute>
            }
          />

          {/* Hostel Matron Dashboard */}
          <Route
            path="/hostel-matron/dashboard/*"
            element={
              <ProtectedRoute roles={["hostel_matron", "admin"]}>
                <HostelMatronDashboard />
              </ProtectedRoute>
            }
          />

          {/* Hostel Custodian Dashboard */}
          <Route
            path="/hostel-custodian/dashboard/*"
            element={
              <ProtectedRoute roles={["hostel_custodian", "admin"]}>
                <HostelCustodianDashboard />
              </ProtectedRoute>
            }
          />

          {/* Kitchen Manager Dashboard */}
          <Route
            path="/kitchen-manager/dashboard/*"
            element={
              <ProtectedRoute roles={["kitchen_manager", "admin"]}>
                <KitchenManagerDashboard />
              </ProtectedRoute>
            }
          />

          {/* Cafeteria Manager Dashboard */}
          <Route
            path="/cafeteria-manager/dashboard/*"
            element={
              <ProtectedRoute roles={["cafeteria_manager", "admin"]}>
                <CafeteriaManagerDashboard />
              </ProtectedRoute>
            }
          />

          {/* Sports Master Dashboard */}
          <Route
            path="/sports-master/dashboard/*"
            element={
              <ProtectedRoute roles={["sports_master", "admin"]}>
                <SportsMasterDashboard />
              </ProtectedRoute>
            }
          />

          {/* Uniform Manager Dashboard */}
          <Route
            path="/uniform-manager/dashboard/*"
            element={
              <ProtectedRoute roles={["uniform_manager", "admin"]}>
                <UniformManagerDashboard />
              </ProtectedRoute>
            }
          />

          {/* Inventory Manager Dashboard */}
          <Route
            path="/inventory-manager/dashboard/*"
            element={
              <ProtectedRoute roles={["inventory_manager", "admin"]}>
                <InventoryManagerDashboard />
              </ProtectedRoute>
            }
          />

          {/* School Counsellor Dashboard */}
          <Route
            path="/counsellor/dashboard/*"
            element={
              <ProtectedRoute roles={["school_counsellor", "admin"]}>
                <SchoolCounsellorDashboard />
              </ProtectedRoute>
            }
          />

          {/* Transport Manager Dashboard */}
          <Route
            path="/transport-manager/dashboard/*"
            element={
              <ProtectedRoute roles={["transport_manager", "admin"]}>
                <TransportManagerDashboard />
              </ProtectedRoute>
            }
          />

          {/* Bus Driver Dashboard */}
          <Route
            path="/bus-driver/dashboard/*"
            element={
              <ProtectedRoute roles={["bus_driver", "admin"]}>
                <BusDriverDashboard />
              </ProtectedRoute>
            }
          />

          {/* Disciplinary Committee Dashboard */}
          <Route
            path="/disciplinary-committee/dashboard/*"
            element={
              <ProtectedRoute roles={["disciplinary_committee", "admin"]}>
                <DisciplinaryCommitteeDashboard />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/apply" element={<PublicAdmissions />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
